/**
 * ISBN check digits, validation and ISBN-10 ↔ ISBN-13 conversion.
 *
 * The rules are the ISBN Users' Manual's (International ISBN Agency, 7th ed.):
 *
 * - ISBN-10: weight the first nine digits 10, 9, …, 2, add the products, and the check
 *   digit is 11 minus the remainder mod 11 — where 10 is written X and 11 is written 0.
 * - ISBN-13: the EAN-13 rule — weights alternate 1, 3, 1, 3 … across the first twelve
 *   digits, and the check digit is 10 minus the remainder mod 10 (10 becomes 0).
 *
 * An ISBN-10 becomes an ISBN-13 by prefixing 978 and recomputing the check digit. Only
 * 978 numbers go back the other way: the 979 prefix was never used for ISBN-10s, so a
 * 979 ISBN has no ten-digit form.
 *
 * Hyphen positions are NOT computed here. Where the hyphens go depends on the
 * registration-group and registrant ranges the International ISBN Agency publishes and
 * revises, so output is hyphen-free unless the reader typed hyphens themselves.
 */

export type IsbnKind = "isbn10" | "isbn13";

/** Why a line is not a valid ISBN, in the order the checks run. */
export type IsbnProblem =
  | "empty"
  | "characters"
  | "length"
  | "x-position"
  | "prefix"
  | "check-digit";

/** One check-digit calculation, laid out for a reader to follow. */
export interface CheckDigitWorking {
  digits: number[];
  weights: number[];
  products: number[];
  sum: number;
  modulus: 10 | 11;
  remainder: number;
  /** The check digit as written: 0–9, or X for an ISBN-10 check of 10. */
  check: string;
}

export interface IsbnAnalysis {
  /** The line as typed, trimmed. */
  input: string;
  /** Digits (and a final X) with labels, hyphens and spaces removed. */
  compact: string;
  kind: IsbnKind | null;
  valid: boolean;
  problem?: IsbnProblem;
  /** The check digit the first 9 or 12 digits call for. */
  expectedCheck?: string;
  /** The working for that check digit. */
  working?: CheckDigitWorking;
  /** Both forms, when the number is valid (no ISBN-10 for a 979 ISBN). */
  isbn10?: string;
  isbn13?: string;
}

const RE_LABEL = /^\s*isbn(?:-?1[03])?\s*[:#]?\s*/i;
const RE_SEPARATORS = /[\s\u2010-\u2015-]/g;
const RE_COMPACT_CHARS = /^[0-9X]+$/;
const RE_ALL_DIGITS = /^\d+$/;
const RE_HYPHEN_GROUPS = /[\s\u2010-\u2015-]+/;

const ISBN10_WEIGHTS = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const ISBN13_WEIGHTS = [1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3];

function workingFor(
  body: string,
  weights: readonly number[],
  modulus: 10 | 11
): CheckDigitWorking {
  const digits = [...body].map(Number);
  const products = digits.map((digit, index) => digit * weights[index]);
  const sum = products.reduce((total, value) => total + value, 0);
  const remainder = sum % modulus;
  const raw = (modulus - remainder) % modulus;
  const check = raw === 10 ? "X" : String(raw);
  return {
    digits,
    weights: [...weights],
    products,
    sum,
    modulus,
    remainder,
    check,
  };
}

/** The working for an ISBN-10's check digit, from its first nine digits. */
export function isbn10Working(firstNine: string): CheckDigitWorking {
  return workingFor(firstNine, ISBN10_WEIGHTS, 11);
}

/** The working for an ISBN-13's (or any EAN-13's) check digit, from its first twelve. */
export function isbn13Working(firstTwelve: string): CheckDigitWorking {
  return workingFor(firstTwelve, ISBN13_WEIGHTS, 10);
}

export function isbn10CheckDigit(firstNine: string): string {
  return isbn10Working(firstNine).check;
}

export function isbn13CheckDigit(firstTwelve: string): string {
  return isbn13Working(firstTwelve).check;
}

/** Strip an "ISBN-13:" style label, hyphens and spaces; upper-case a final x. */
export function compactIsbn(raw: string): string {
  return raw.replace(RE_LABEL, "").replace(RE_SEPARATORS, "").toUpperCase();
}

/** 0306406152 → 9780306406157. Expects a valid ISBN-10. */
export function isbn10To13(isbn10: string): string {
  const body = `978${isbn10.slice(0, 9)}`;
  return `${body}${isbn13CheckDigit(body)}`;
}

/** 9780306406157 → 0306406152; null for a 979 ISBN, which has no ten-digit form. */
export function isbn13To10(isbn13: string): string | null {
  if (!isbn13.startsWith("978")) {
    return null;
  }
  const body = isbn13.slice(3, 12);
  return `${body}${isbn10CheckDigit(body)}`;
}

function analyzeTen(input: string, compact: string): IsbnAnalysis {
  if (compact.slice(0, 9).includes("X")) {
    return {
      input,
      compact,
      kind: "isbn10",
      valid: false,
      problem: "x-position",
    };
  }
  const working = isbn10Working(compact.slice(0, 9));
  const base = { input, compact, kind: "isbn10" as const, working };
  if (working.check !== compact[9]) {
    return {
      ...base,
      valid: false,
      problem: "check-digit",
      expectedCheck: working.check,
    };
  }
  return {
    ...base,
    valid: true,
    expectedCheck: working.check,
    isbn10: compact,
    isbn13: isbn10To13(compact),
  };
}

function analyzeThirteen(input: string, compact: string): IsbnAnalysis {
  if (!RE_ALL_DIGITS.test(compact)) {
    return {
      input,
      compact,
      kind: "isbn13",
      valid: false,
      problem: "x-position",
    };
  }
  const working = isbn13Working(compact.slice(0, 12));
  const base = { input, compact, kind: "isbn13" as const, working };
  if (!(compact.startsWith("978") || compact.startsWith("979"))) {
    return {
      ...base,
      valid: false,
      problem: "prefix",
      expectedCheck: working.check,
    };
  }
  if (working.check !== compact[12]) {
    return {
      ...base,
      valid: false,
      problem: "check-digit",
      expectedCheck: working.check,
    };
  }
  return {
    ...base,
    valid: true,
    expectedCheck: working.check,
    isbn13: compact,
    isbn10: isbn13To10(compact) ?? undefined,
  };
}

/** Read one line as an ISBN-10 or ISBN-13 and say exactly what is wrong with it. */
export function analyzeIsbn(raw: string): IsbnAnalysis {
  const input = raw.trim();
  const compact = compactIsbn(input);
  if (compact === "") {
    return { input, compact, kind: null, valid: false, problem: "empty" };
  }
  if (!RE_COMPACT_CHARS.test(compact)) {
    return { input, compact, kind: null, valid: false, problem: "characters" };
  }
  if (compact.length === 10) {
    return analyzeTen(input, compact);
  }
  if (compact.length === 13) {
    return analyzeThirteen(input, compact);
  }
  return { input, compact, kind: null, valid: false, problem: "length" };
}

/**
 * The ISBN-13 with the reader's own hyphens, when they typed a well-formed set.
 *
 * "0-306-40615-2" becomes "978-0-306-40615-7": the ten-digit groups are kept, 978 is
 * prepended and the check digit recomputed. An ISBN-13 keeps its five groups as typed.
 * Anything else — no hyphens, the wrong number of groups, a check digit that is not a
 * group of its own — returns null, because hyphen positions cannot be guessed.
 */
export function hyphenatedIsbn13(raw: string): string | null {
  const analysis = analyzeIsbn(raw);
  if (!(analysis.valid && analysis.isbn13)) {
    return null;
  }
  const groups = raw
    .replace(RE_LABEL, "")
    .trim()
    .split(RE_HYPHEN_GROUPS)
    .filter(Boolean)
    .map((group) => group.toUpperCase());
  const last = groups.at(-1);
  if (!last || last.length !== 1) {
    return null;
  }
  if (analysis.kind === "isbn13" && groups.length === 5) {
    return groups.join("-");
  }
  if (analysis.kind === "isbn10" && groups.length === 4) {
    return ["978", ...groups.slice(0, 3), analysis.isbn13.slice(-1)].join("-");
  }
  return null;
}
